import type { Config } from "wagmi"
import { encodeFunctionData, erc20Abi, isAddress, parseUnits, type Address, type Hash, type Hex, type TransactionReceipt } from "viem"
import { getPublicClient, readContract, sendTransaction, waitForTransactionReceipt } from "@wagmi/core"

import type { ApprovalMode } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { IWalletConnectionStore } from "@/domains/wallet/types"

import type { TokenStore } from "@/domains/tokens"
import { wrapNative, unwrapNative } from "@/domains/on-chain"
import { determineApprovalAmount } from "@/domains/library"

import type {
  ISwapAggregatorPluginRegistry,
  ISwapAggregatorSettingsStore,
  ISwapExecutionStore,
  ISwapInputStore,
  ISwapOrchestrator,
  ISwapQuoteStore,
} from "./interfaces"
import {
  SwapApprovalCompletedEvent,
  SwapApprovalStartedEvent,
  SwapCompletedEvent,
  SwapErrorEvent,
  SwapExecutionStartedEvent,
} from "./events"
import type { NormalizedSwapQuote } from "./types"
import { getSwapActionMode } from "./swap-mode"

/**
 * Swap Orchestrator
 * Coordinates swap execution (quote fetching is handled by aggregators independently)
 * Uses interfaces for all dependencies
 */
export class SwapOrchestrator implements ISwapOrchestrator {
  constructor(
    private readonly quoteStore: ISwapQuoteStore,
    private readonly executionStore: ISwapExecutionStore,
    private readonly pluginRegistry: ISwapAggregatorPluginRegistry,
    private readonly eventBus: IEventBus,
    private readonly walletStore: IWalletConnectionStore,
    private readonly settingsStore: ISwapAggregatorSettingsStore,
    private readonly nativeToken: TokenStore,
    private readonly wrappedNativeToken: TokenStore,
    private readonly inputStore: ISwapInputStore,
  ) {}

  /**
   * Execute swap, wrap, or unwrap based on current input/output tokens
   * For ETH↔WETH pairs, uses WETH contract directly; otherwise routes to swap aggregator
   */
  async execute(config: Config): Promise<void> {
    const mode = getSwapActionMode(
      this.inputStore.inputToken,
      this.inputStore.outputToken,
      this.nativeToken,
      this.wrappedNativeToken,
    )
    if (mode === "wrap" || mode === "unwrap") {
      await this.executeWrapUnwrap(config, mode)
      return
    }
    const selectedAggregatorId = this.quoteStore.selectedQuote?.aggregatorId ?? this.quoteStore.selectedAggregatorId
    if (!selectedAggregatorId) {
      this.executionStore.setError("No route selected")
      this.eventBus.publish(new SwapErrorEvent(null, "No route selected"))
      return
    }
    await this.executeSwap(selectedAggregatorId, config)
  }

  /**
   * Execute wrap (ETH→WETH) or unwrap (WETH→ETH) via WETH contract
   */
  private async executeWrapUnwrap(config: Config, mode: "wrap" | "unwrap"): Promise<void> {
    const userAddress = this.walletStore.getConnectedAddress()
    if (!userAddress) {
      this.executionStore.setError("No wallet connected")
      this.eventBus.publish(new SwapErrorEvent(null, "No wallet connected"))
      return
    }
    const inputToken = this.inputStore.inputToken
    const outputToken = this.inputStore.outputToken
    const inputAmount = this.inputStore.inputAmount
    if (!inputToken || !outputToken || !inputAmount || parseFloat(inputAmount) <= 0) {
      this.executionStore.setError("Invalid amount")
      this.eventBus.publish(new SwapErrorEvent(null, "Invalid amount"))
      return
    }
    const chainId = inputToken.chainId
    const amountRaw = parseUnits(inputAmount, inputToken.decimals)
    const wethAddress = this.wrappedNativeToken.address.toString() as `0x${string}`

    this.executionStore.startSwap()
    const routeLabel = "WETH"
    this.eventBus.publish(
      new SwapExecutionStartedEvent({
        aggregatorId: routeLabel,
        quoteId: "direct",
        inputToken,
        outputToken,
        inputAmountRaw: amountRaw,
        outputAmountRaw: amountRaw,
        inputAmountFormatted: inputAmount,
        outputAmountFormatted: inputAmount,
        exchangeRate: "1",
        priceImpactBps: null,
        gasEstimate: null,
        transactionData: null,
        transactionMetadata: {},
        timestamp: Date.now(),
      }),
    )

    const result =
      mode === "wrap"
        ? await wrapNative({
            account: userAddress,
            chainId,
            config,
            nativeAddress: wethAddress,
            amount: amountRaw,
          })
        : await unwrapNative({
            account: userAddress,
            chainId,
            config,
            nativeAddress: wethAddress,
            amount: amountRaw,
          })

    if (result.success && result.tx) {
      this.executionStore.complete()
      this.eventBus.publish(
        new SwapCompletedEvent(
          {
            aggregatorId: routeLabel,
            quoteId: "direct",
            inputToken,
            outputToken,
            inputAmountRaw: amountRaw,
            outputAmountRaw: amountRaw,
            inputAmountFormatted: inputAmount,
            outputAmountFormatted: inputAmount,
            exchangeRate: "1",
            priceImpactBps: null,
            gasEstimate: null,
            transactionData: null,
            transactionMetadata: {},
            timestamp: Date.now(),
          },
          result.tx,
        ),
      )
    } else {
      const errorMessage = result.success === false ? (result.error as string) : "Unknown error"
      this.executionStore.setError(errorMessage)
      this.eventBus.publish(new SwapErrorEvent(null, `${mode === "wrap" ? "Wrap" : "Unwrap"} failed: ${errorMessage}`))
    }
  }

  /**
   * Execute swap transaction via aggregator
   * Gets selected aggregator from QuoteStore and calls its execution methods
   */
  async executeSwap(aggregatorId: string, config: Config): Promise<void> {
    try {
      // Get user address dynamically
      const userAddress = this.walletStore.getConnectedAddress()
      if (!userAddress) {
        throw new Error("No wallet connected")
      }

      // Get selected aggregator
      const aggregator = this.pluginRegistry.get(aggregatorId)
      if (!aggregator) {
        throw new Error(`Aggregator ${aggregatorId} not found`)
      }

      // Get selected quote
      const quote = this.quoteStore.selectedQuote
      if (!quote) {
        throw new Error("No quote selected")
      }

      // Get chainId from quote
      const chainId = quote.inputToken.chainId

      const swapTx = await aggregator.executeSwapTransaction(quote, userAddress)
      if (!swapTx) {
        throw new Error("Failed to build swap transaction")
      }

      const approvalSpender = this.resolveApprovalSpender(quote, swapTx.to)

      // Native ETH does not require allowance - it is sent via msg.value, not transferFrom
      const isInputNative =
        quote.inputToken.address.equalsString(this.nativeToken.address.toString())

      if (!isInputNative) {
        const currentBalance = await this.getCurrentTokenBalance(config, quote.inputToken, userAddress, chainId)
        if (currentBalance !== null && currentBalance < quote.inputAmountRaw) {
          throw new Error(`Insufficient ${quote.inputToken.symbol} balance`)
        }
      }

      // Check current allowance only for ERC20 tokens
      const currentAllowance = isInputNative
        ? quote.inputAmountRaw // Skip approval - treat as already "approved"
        : await this.getCurrentAllowance(
            config,
            quote.inputToken,
            approvalSpender,
            userAddress,
            chainId,
          )

      // Execute approval if needed (never for native token)
      if (!isInputNative && currentAllowance < quote.inputAmountRaw) {
        this.executionStore.startApproval()
        this.eventBus.publish(new SwapApprovalStartedEvent(quote))

        // Get approval mode from settings (real-time access, MobX observable)
        const approvalMode = this.settingsStore.approvalMode

        const approvalTx = this.buildApprovalTransaction(quote.inputToken, approvalSpender, quote.inputAmountRaw, approvalMode)

        // Execute approval transaction (based on old code pattern)
        try {
          const approvalHash = await sendTransaction(config, {
            account: userAddress,
            chainId,
            to: approvalTx.to,
            data: approvalTx.data,
            value: approvalTx.value,
          })

          // Wait for approval confirmation
          const approvalReceipt = await this.waitForFastReceipt(config, approvalHash, chainId)
          if (approvalReceipt.status === "reverted") {
            throw new Error("Approval transaction reverted")
          }

          // Publish approval completed event
          this.eventBus.publish(new SwapApprovalCompletedEvent(quote, approvalReceipt.transactionHash))
        } catch (approvalError) {
          const errorMessage = approvalError instanceof Error ? approvalError.message : "Unknown error"
          this.eventBus.publish(new SwapErrorEvent(quote, `Approval failed: ${errorMessage}`))
          throw approvalError
        }
      }

      // Execute swap
      this.executionStore.startSwap()
      this.eventBus.publish(new SwapExecutionStartedEvent(quote))

      // Execute swap transaction (based on old code pattern from swap-via-encoded-data.ts)
      try {
        const swapHash = await sendTransaction(config, {
          account: userAddress,
          chainId,
          to: swapTx.to,
          data: swapTx.data,
          value: swapTx.value,
        })

        // Wait for swap confirmation
        const swapReceipt = await this.waitForFastReceipt(config, swapHash, chainId)
        if (swapReceipt.status === "reverted") {
          throw new Error("Swap transaction reverted")
        }

        // Update execution store and publish completion event
        this.executionStore.complete()
        this.eventBus.publish(new SwapCompletedEvent(quote, swapReceipt.transactionHash))
      } catch (swapError) {
        const errorMessage = swapError instanceof Error ? swapError.message : "Unknown error"
        this.executionStore.setError(errorMessage)
        this.eventBus.publish(new SwapErrorEvent(quote, `Swap failed: ${errorMessage}`))
        throw swapError
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error"
      this.executionStore.setError(errorMessage)
      const quote = this.quoteStore.selectedQuote
      this.eventBus.publish(new SwapErrorEvent(quote ?? null, errorMessage))
    }
  }

  private async waitForFastReceipt(config: Config, hash: Hash, chainId: number): Promise<TransactionReceipt> {
    const publicClient = getPublicClient(config, { chainId })
    const startedAt = Date.now()

    if (publicClient) {
      while (Date.now() - startedAt < 20_000) {
        const receipt = await publicClient.getTransactionReceipt({ hash }).catch(() => null)
        if (receipt) {
          return receipt
        }
        await sleep(250)
      }
    }

    return await waitForTransactionReceipt(config, {
      hash,
      pollingInterval: 500,
      timeout: 45_000,
    })
  }

  private async getCurrentTokenBalance(
    config: Config,
    token: TokenStore,
    userAddress: `0x${string}`,
    chainId: number,
  ): Promise<bigint | null> {
    try {
      return (await readContract(config, {
        address: token.address.toViemAddress(),
        abi: erc20Abi,
        functionName: "balanceOf",
        args: [userAddress],
        chainId,
      })) as bigint
    } catch {
      return null
    }
  }

  private async getCurrentAllowance(
    config: Config,
    token: TokenStore,
    spender: Address,
    userAddress: Address,
    chainId: number,
  ): Promise<bigint> {
    return (await readContract(config, {
      address: token.address.toViemAddress(),
      abi: erc20Abi,
      functionName: "allowance",
      args: [userAddress, spender],
      chainId,
    })) as bigint
  }

  private buildApprovalTransaction(
    token: TokenStore,
    spender: Address,
    inputAmountRaw: bigint,
    approvalMode: ApprovalMode,
  ): { to: Address; data: Hex; value: 0n } {
    return {
      to: token.address.toViemAddress(),
      data: encodeFunctionData({
        abi: erc20Abi,
        functionName: "approve",
        args: [spender, determineApprovalAmount(inputAmountRaw, approvalMode, "immutable")],
      }),
      value: 0n,
    }
  }

  private resolveApprovalSpender(quote: NormalizedSwapQuote, swapTarget: Address): Address {
    const metadata = quote.transactionMetadata as Record<string, unknown>
    const spender = [
      metadata.approvalAddress,
      metadata.spenderAddress,
      metadata.allowanceTarget,
      metadata.targetAddress,
    ].find((value): value is Address => typeof value === "string" && isAddress(value))

    return spender ?? swapTarget
  }
}

const sleep = (milliseconds: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, milliseconds))
